import { Link, useNavigate } from 'react-router-dom';
import { Package, Server, ExternalLink, Zap } from 'lucide-react';
import { nip19 } from 'nostr-tools';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useSkills } from '@/hooks/useSkills';
import { useAuthor } from '@/hooks/useAuthor';
import { formatPrice, type Skill, type SkillType } from '@/lib/skills';
import { genUserName } from '@/lib/genUserName';

interface SkillBrowserProps {
  type?: SkillType;
  author?: string;
  tag?: string;
  search?: string;
  limit?: number;
  compact?: boolean;
}

export function SkillBrowser({ type, author, tag, search, limit = 20, compact = false }: SkillBrowserProps) {
  const tags = tag ? [tag] : undefined;
  const { data: skills, isLoading, error } = useSkills({ type, author, tags, search, limit });

  if (isLoading) {
    return (
      <div className={`grid gap-4 ${compact ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2'}`}>
        {Array.from({ length: compact ? 6 : 4 }).map((_, i) => (
          <SkillCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">Failed to load skills. Check your relay connection.</p>
        </CardContent>
      </Card>
    );
  }

  if (!skills || skills.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 text-center">
          <div className="max-w-sm mx-auto space-y-4">
            <Package className="w-12 h-12 mx-auto text-muted-foreground/50" />
            <p className="text-muted-foreground">
              No skills found. Be the first to publish!
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`grid gap-4 ${compact ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2'}`}>
      {skills.map((skill) => (
        <SkillCard key={skill.id} skill={skill} compact={compact} activeTag={tag} />
      ))}
    </div>
  );
}

function SkillCard({ skill, compact, activeTag }: { skill: Skill; compact?: boolean; activeTag?: string }) {
  const navigate = useNavigate();
  const author = useAuthor(skill.pubkey);
  const authorName = author.data?.metadata?.name || genUserName(skill.pubkey);
  const npub = nip19.npubEncode(skill.pubkey);

  return (
    <Link to={`/skill/${skill.pubkey}/${skill.identifier}`}>
      <Card className="h-full glass-card hover:glass-card-hover transition-all duration-300 group cursor-pointer">
        <CardHeader className={compact ? 'pb-2' : ''}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`shrink-0 rounded-xl flex items-center justify-center border border-white/[0.08] group-hover:scale-105 transition-transform duration-300 ${skill.type === 'mcp' ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 text-emerald-400' : 'bg-gradient-to-br from-orange-500/20 to-amber-500/20 text-orange-400'} ${compact ? 'w-10 h-10' : 'w-12 h-12'}`}>
                {skill.type === 'mcp' ? <Server className="w-5 h-5" /> : <Package className="w-5 h-5" />}
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold truncate group-hover:text-orange-400 transition-colors">
                  {skill.name}
                </h3>
                <Link
                  to={`/profile/${npub}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs text-muted-foreground truncate hover:text-orange-400 transition-colors"
                >
                  by {authorName}
                </Link>
              </div>
            </div>
            <Badge variant={skill.price > 0 ? 'default' : 'secondary'} className={`shrink-0 ${skill.price > 0 ? 'bg-gradient-to-r from-orange-500/80 to-amber-500/80 border-0' : 'bg-white/[0.05] border-white/[0.08]'}`}>
              {skill.price > 0 && <Zap className="w-3 h-3 mr-1" />}
              {formatPrice(skill.price)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className={compact ? 'pt-0' : ''}>
          <p className={`text-sm text-muted-foreground ${compact ? 'line-clamp-2' : 'line-clamp-3'}`}>
            {skill.description}
          </p>
          {!compact && skill.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {skill.tags.slice(0, 4).map((t) => (
                <Badge
                  key={t}
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/browse?tag=${encodeURIComponent(t)}`);
                  }}
                  className={`text-xs cursor-pointer transition-colors ${
                    activeTag === t
                      ? 'bg-orange-500/20 border-orange-500/50 text-orange-400'
                      : 'bg-white/[0.02] border-white/[0.08] hover:bg-orange-500/10 hover:border-orange-500/30 hover:text-orange-400'
                  }`}
                >
                  {t}
                </Badge>
              ))}
              {skill.tags.length > 4 && (
                <Badge variant="outline" className="text-xs bg-white/[0.02] border-white/[0.08]">
                  +{skill.tags.length - 4}
                </Badge>
              )}
            </div>
          )}
          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
            <span>v{skill.version}</span>
            {skill.repository && (
              <span className="flex items-center gap-1">
                <ExternalLink className="w-3 h-3" />
                Source
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function SkillCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-3">
          <Skeleton className="w-12 h-12 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      </CardContent>
    </Card>
  );
}
